/**
 * Datos de la empresa: nombre, NIF y dirección.
 *
 * Existe por una razón concreta: el informe del art. 34.9 se niega a generarse
 * sin NIF, y las empresas dadas de alta antes de que fuera obligatorio no lo
 * tienen. Sin esta pantalla ese 409 no se podría arreglar desde la aplicación
 * — un error que la interfaz no puede resolver es un producto roto.
 */
import { useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { getTenantStatus, updateTenant, TenantStatus, apiMessage } from '../../services/apiClient';
import { Icon } from '../ui/Icon';

export function CompanyAdmin() {
  const [tenant, setTenant] = useState<TenantStatus | null>(null);
  const [name, setName] = useState('');
  const [cif, setCif] = useState('');
  const [address, setAddress] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getTenantStatus()
      .then((t) => {
        setTenant(t);
        setName(t.name);
        setCif(t.cif ?? '');
        setAddress(t.address ?? '');
      })
      .catch(() => setError('No se pudieron cargar los datos de la empresa'));
  }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      const updated = await updateTenant({
        name: name.trim(),
        cif: cif.trim(),
        address: address.trim(),
      });
      setTenant(updated);
      setSaved(true);
    } catch (err) {
      setError(apiMessage(err, 'No se pudieron guardar los cambios'));
    } finally {
      setSaving(false);
    }
  };

  const faltaNif = tenant != null && !(tenant.cif ?? '').trim();

  return (
    <AdminLayout title="Empresa" subtitle="Los datos que van en el informe para Inspección">
      <form onSubmit={save} className="max-w-[560px] flex flex-col gap-4">
        {faltaNif && (
          <div className="flex items-start gap-3 rounded-2xl bg-signal-soft border border-signal px-4 py-3">
            <Icon name="alert" size={20} strokeWidth={1.8} className="text-signal shrink-0 mt-0.5" />
            <p className="text-[14.5px] text-ink-2">
              Falta el NIF. El informe del registro de jornada identifica a la empresa
              obligada, así que <strong>no se puede emitir</strong> hasta que lo pongas.
            </p>
          </div>
        )}

        {error && (
          <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>
        )}
        {saved && !error && (
          <p className="rounded-xl bg-brand-soft text-brand px-4 py-3 text-[14px]">Guardado.</p>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="label !mb-0">Nombre de la empresa</span>
          <input
            className="field bg-surface"
            value={name}
            minLength={2}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="label !mb-0">NIF</span>
          <input
            className="field bg-surface"
            placeholder="B12345678"
            value={cif}
            minLength={6}
            maxLength={20}
            onChange={(e) => setCif(e.target.value)}
          />
          <span className="text-[13px] text-ink-2">
            Cada empresa lleva su propio registro. Si gestionas varias sociedades, cada una
            necesita su cuenta.
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="label !mb-0">Dirección fiscal</span>
          <input
            className="field bg-surface"
            placeholder="Calle, número — Código postal, población"
            value={address}
            maxLength={300}
            onChange={(e) => setAddress(e.target.value)}
          />
        </label>

        {tenant?.jurisdiction && (
          <p className="text-[13.5px] text-ink-2">
            Legislación aplicada: <strong>{tenant.jurisdiction.name}</strong>
            {tenant.jurisdiction.national_pending &&
              ' — la ley nacional de tu país está codificada pero pendiente de validar, así que de momento se aplican los mínimos europeos.'}
          </p>
        )}

        <button type="submit" className="btn-primary self-start" disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </form>
    </AdminLayout>
  );
}
